import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import {
  Cart,
  Heart,
  List,
  Person,
  Search as SearchIcon,
  X,
} from 'react-bootstrap-icons';
import Swal from 'sweetalert2';
import Cookies from 'universal-cookie';

import Categorysection from './Categorysection';
import SearchBox from './SearchBox';
import LoginModal from './LoginModal';
import SignupModal from './SignupModal';
import CartModal from './CartModal';
import useAuth, { clearSession } from '../../hooks/useAuth';
// The original artwork is a 500x500 canvas whose wordmark fills only the
// middle ~40%, so at header size it rendered too small to read. This is
// the same mark trimmed to its ink, on a transparent background.
import logoFull from '../../images/logo-wordmark.png';
import './Header.scss';

const MOBILE_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/allproducts', label: 'All products' },
  { to: '/all-categories', label: 'Categories' },
  { to: '/wishlist', label: 'Wishlist' },
  { to: '/aboutus', label: 'About us' },
  { to: '/tac', label: 'Terms and conditions' },
];

const CONTACT_LINKS = [
  { href: 'mailto:indianjewelryish@gmail.com', label: 'Write an e-mail' },
  { href: 'tel:+919799279134', label: 'Call for help' },
  {
    href: 'https://wa.me/+919799279134',
    label: 'Chat on WhatsApp',
    external: true,
  },
];

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn, userName } = useAuth();

  const isCheckOutPage = location.pathname.startsWith('/check/checkout');
  const isLandingPage = location.pathname === '/';

  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [loginShow, setLoginShow] = useState(false);
  const [signupShow, setSignupShow] = useState(false);
  const [showCart, setShowCart] = useState(false);

  const menuButtonRef = useRef(null);

  // A stale cookie means the session expired in another tab — drop the
  // leftover localStorage entries so the UI does not claim to be signed in.
  useEffect(() => {
    const cookies = new Cookies();
    if (!cookies.get('user_id') && localStorage.getItem('token')) {
      clearSession();
    }
  }, [location.pathname]);

  // Close transient UI whenever the route changes.
  useEffect(() => {
    setMenuOpen(false);
    setMobileSearchOpen(false);
    setContactOpen(false);
  }, [location.pathname, location.search]);

  // Lock background scrolling while the drawer is open, and restore it on
  // unmount so a mid-animation navigation can't leave the page stuck.
  useEffect(() => {
    if (!menuOpen) return undefined;

    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  // Return focus to the trigger when the drawer closes (keyboard users).
  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    menuButtonRef.current?.focus();
  }, []);

  const closeModals = useCallback(() => {
    setLoginShow(false);
    setSignupShow(false);
    setShowCart(false);
  }, []);

  const handleLogout = useCallback(() => {
    clearSession();
    setMenuOpen(false);
    if (isCheckOutPage) navigate('/');
    Swal.fire({
      icon: 'success',
      title: 'You have been signed out',
      confirmButtonColor: '#b63f23',
    });
  }, [isCheckOutPage, navigate]);

  const navLinkClass = ({ isActive }) =>
    `ij-header__nav-link${isActive ? ' is-active' : ''}`;

  return (
    <>
      <header className="ij-header">
        <div className="ij-header__inner">
          {/* ---------- Mobile: menu trigger ---------- */}
          <button
            type="button"
            ref={menuButtonRef}
            className="ij-header__icon-btn ij-header__menu-trigger"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            aria-controls="ij-mobile-menu"
            onClick={() => setMenuOpen(true)}
          >
            <List size={24} aria-hidden="true" />
          </button>

          {/* ---------- Logo ---------- */}
          <Link to="/" className="ij-header__logo" aria-label="Indian Jewelryish — home">
            <img
              src={logoFull}
              alt="Indian Jewelryish"
              width="346"
              height="210"
              fetchpriority="high"
            />
          </Link>

          {/* ---------- Desktop search ---------- */}
          <div className="ij-header__search">
            <SearchBox />
          </div>

          {/* ---------- Desktop nav ---------- */}
          <nav className="ij-header__nav" aria-label="Main">
            <NavLink to="/" className={navLinkClass} end>
              Home
            </NavLink>
            <NavLink to="/allproducts" className={navLinkClass}>
              Shop
            </NavLink>
            <NavLink to="/wishlist" className={navLinkClass}>
              Wishlist
            </NavLink>

            <Dropdown align="end" className="ij-header__account">
              <Dropdown.Toggle
                as="button"
                type="button"
                className="ij-header__nav-link ij-header__account-toggle"
                id="ij-account-menu"
              >
                <Person size={16} aria-hidden="true" />
                <span>{isLoggedIn ? userName || 'Account' : 'Account'}</span>
              </Dropdown.Toggle>

              <Dropdown.Menu className="ij-header__account-menu">
                {isLoggedIn ? (
                  <>
                    <Dropdown.Header>My account</Dropdown.Header>
                    <Dropdown.Item as={Link} to="/check/profile">
                      Profile
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/check/profile/your-orders">
                      Your orders
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item as="button" onClick={handleLogout}>
                      Sign out
                    </Dropdown.Item>
                  </>
                ) : (
                  <>
                    <Dropdown.Header>
                      Sign in to access your account
                    </Dropdown.Header>
                    <div className="ij-header__account-actions">
                      <button
                        type="button"
                        className="ij-btn ij-btn--primary ij-btn--sm ij-btn--block"
                        onClick={() => setLoginShow(true)}
                      >
                        Sign in
                      </button>
                      <button
                        type="button"
                        className="ij-btn ij-btn--outline ij-btn--sm ij-btn--block"
                        onClick={() => setSignupShow(true)}
                      >
                        Create account
                      </button>
                    </div>
                  </>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </nav>

          {/* ---------- Actions (all viewports) ---------- */}
          <div className="ij-header__actions">
            <button
              type="button"
              className="ij-header__icon-btn ij-header__search-trigger"
              aria-label={mobileSearchOpen ? 'Close search' : 'Open search'}
              aria-expanded={mobileSearchOpen}
              onClick={() => setMobileSearchOpen((open) => !open)}
            >
              {mobileSearchOpen ? (
                <X size={22} aria-hidden="true" />
              ) : (
                <SearchIcon size={19} aria-hidden="true" />
              )}
            </button>

            <Link
              to="/wishlist"
              className="ij-header__icon-btn ij-header__wishlist-link"
              aria-label="Wishlist"
            >
              <Heart size={19} aria-hidden="true" />
            </Link>

            <button
              type="button"
              className="ij-header__icon-btn"
              aria-label="Open cart"
              onClick={() => setShowCart(true)}
            >
              <Cart size={19} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* ---------- Mobile search drawer ---------- */}
        {mobileSearchOpen && (
          <div className="ij-header__mobile-search">
            <SearchBox autoFocus onNavigate={() => setMobileSearchOpen(false)} />
          </div>
        )}
      </header>

      {/* ---------- Mobile menu ---------- */}
      {menuOpen && (
        <div className="ij-drawer" role="presentation">
          <div
            className="ij-drawer__scrim"
            onClick={closeMenu}
            aria-hidden="true"
          />
          <div
            className="ij-drawer__panel"
            id="ij-mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
          >
            <div className="ij-drawer__header">
              <span className="ij-drawer__title">Menu</span>
              <button
                type="button"
                className="ij-header__icon-btn"
                aria-label="Close menu"
                onClick={closeMenu}
                autoFocus
              >
                <X size={26} aria-hidden="true" />
              </button>
            </div>

            <nav className="ij-drawer__nav" aria-label="Mobile">
              {MOBILE_LINKS.map(({ to, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === '/'}
                  className={({ isActive }) =>
                    `ij-drawer__link${isActive ? ' is-active' : ''}`
                  }
                  onClick={() => setMenuOpen(false)}
                >
                  {label}
                </NavLink>
              ))}

              <button
                type="button"
                className="ij-drawer__link ij-drawer__disclosure"
                aria-expanded={contactOpen}
                onClick={() => setContactOpen((open) => !open)}
              >
                Contact us
                <span
                  className={`ij-drawer__chevron${contactOpen ? ' is-open' : ''}`}
                  aria-hidden="true"
                />
              </button>

              {contactOpen && (
                <div className="ij-drawer__sublist">
                  {CONTACT_LINKS.map(({ href, label, external }) => (
                    <a
                      key={href}
                      href={href}
                      className="ij-drawer__sublink"
                      {...(external
                        ? { target: '_blank', rel: 'noopener noreferrer' }
                        : {})}
                    >
                      {label}
                    </a>
                  ))}
                </div>
              )}
            </nav>

            <div className="ij-drawer__footer">
              {isLoggedIn ? (
                <>
                  <Link
                    to="/check/profile"
                    className="ij-btn ij-btn--primary ij-btn--block"
                    onClick={() => setMenuOpen(false)}
                  >
                    {userName ? `Profile — ${userName}` : 'Profile'}
                  </Link>
                  <button
                    type="button"
                    className="ij-btn ij-btn--outline ij-btn--block"
                    onClick={handleLogout}
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="ij-btn ij-btn--primary ij-btn--block"
                    onClick={() => {
                      setMenuOpen(false);
                      setLoginShow(true);
                    }}
                  >
                    Sign in
                  </button>
                  <button
                    type="button"
                    className="ij-btn ij-btn--outline ij-btn--block"
                    onClick={() => {
                      setMenuOpen(false);
                      setSignupShow(true);
                    }}
                  >
                    Create account
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* The category rail sits under the header on every page. */}
      <Categorysection collapsedOnMobile={isLandingPage} />

      <LoginModal show={loginShow} onClose={closeModals} />
      <SignupModal show={signupShow} onClose={closeModals} />
      <CartModal show={showCart} onClose={closeModals} />
    </>
  );
};

export default Header;
